import { getAPI, postAPI, deleteAPI } from 'services/api/apiAsync';
import { refreshApToken } from 'services/session/sessionAsync';
import { fetchChildItems } from 'services/child-items/childItemsAsync';
import { logErrors } from 'utils';
import type { AppDispatch, RootState } from 'app/store';
import { API_ENDPOINTS } from 'utils/constants';
import { setInvitor } from './invitorSlice';
import { selectInvitorLoaded } from './invitorSelectors';
import type { Invitor } from './types';

/* Delay between an add-/delete-child mutation and the token refresh
 * that follows it. The backend needs a short window to update the
 * JWT's `itps:administered_users` claim; refreshing immediately gets
 * the new token with the OLD claim set, and any subsequent
 * `/monitored_items/dependents/children` call keeps returning the
 * stale list. Tune here if backend latency changes. */
const TOKEN_REFRESH_DELAY_MS = 3000;

// GET /api/client/invitors → the Invitor (with participants[]). Mirrors Angular
// WatchlistService.getInvitorAndStore(): caches (skips if already loaded) and
// swallows errors (e.g. 404 for individual/non-family users) so the store
// simply stays empty.
// Concurrent callers that mount before the first response share one request.
let inFlightInvitorFetch: Promise<void> | null = null;

export default function fetchInvitor() {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    if (selectInvitorLoaded(getState())) return;
    if (inFlightInvitorFetch) {
      await inFlightInvitorFetch;
      return;
    }
    inFlightInvitorFetch = (async () => {
      try {
        const response = await dispatch(
          getAPI({ url: API_ENDPOINTS.invitors }),
        ).unwrap();
        dispatch(setInvitor((response as Invitor) ?? null));
      } catch {
        // Swallowed — no invitor for this user (matches Angular's no-error-handler behavior).
      } finally {
        inFlightInvitorFetch = null;
      }
    })();
    await inFlightInvitorFetch;
  };
}

// Mutation payload shapes — mirror Angular MyAccountService methods
// (my-account.service.ts adult/child/newAdult endpoints).

// Mirrors Angular's `RelatedAdult` domain model
// (`my-account/domain/related-adult.ts`) — the API's Jackson DTO
// expects the nested `name: { first, last }` shape (same story as
// `AddChildPayload`; flat `firstName` / `lastName` triggers an
// "Unrecognized field" error).
export interface InviteAdultPayload {
  name: { first: string; last: string };
  emailAddress: string;
  relationship: string;
}

// Mirrors Angular's `RelatedChild` domain model
// (`my-account/domain/related-child.ts`) — the API's Jackson DTO
// rejects flat `firstName` / `lastName` fields with
// "Unrecognized field 'firstName', not marked as ignorable" and
// expects the nested `name: { first, last }` shape plus
// `relationship: 'DEPENDENT'`.
export interface AddChildPayload {
  name: { first: string; last: string };
  birthDate: string;
  ssn: string;
  authorization: string;
  relationship: string;
  // ISO `YYYY-MM-DD` sourced from the product offer
  // (`selectOfferServiceEndDateIso`). Only populated on the
  // Minor Defense use-code path; the ordinary add-child flow sends
  // `null` here. Without this on the child record for a MinorDefense
  // use-code redemption, the BE will not trigger the "Activate your
  // IDD Secure Start" activation email.
  serviceEndDate: string | null;
  // Optional MinorDefense use-code carried through from UseCodeScreen.
  useCode?: string;
}

export interface InviteNewAdultPayload {
  agedOutUserId: string;
  emailAddress: string;
}

// POST /api/client/invitors/adults → sends the invite email and returns the
// updated Invitor with the newly-invited adult appended to participants.
export function inviteAdult(payload: InviteAdultPayload) {
  return async (dispatch: AppDispatch) => {
    const response = await dispatch(
      postAPI({ url: API_ENDPOINTS.invitorsAdults, data: payload }),
    ).unwrap();
    dispatch(setInvitor((response as Invitor) ?? null));
    return response as Invitor;
  };
}

// DELETE /api/client/invitors/adults/{id} → removes the adult (or cancels the
// pending invite) and returns the updated Invitor.
export function deleteAdult(participantId: string) {
  return async (dispatch: AppDispatch) => {
    const response = await dispatch(
      deleteAPI({
        url: `${API_ENDPOINTS.invitorsAdultById}/${participantId}`,
      }),
    ).unwrap();
    dispatch(setInvitor((response as Invitor) ?? null));
    return response as Invitor;
  };
}

// POST /api/client/invitors/children → enrolls the child in the family plan
// and returns the updated Invitor.
export function addChild(payload: AddChildPayload) {
  return async (dispatch: AppDispatch) => {
    const response = await dispatch(
      postAPI({ url: API_ENDPOINTS.invitorsChildren, data: payload }),
    ).unwrap();
    dispatch(setInvitor((response as Invitor) ?? null));
    /* Fire-and-forget token refresh after `TOKEN_REFRESH_DELAY_MS` so
     * the new JWT's `itps:administered_users` claim picks up the just-
     * added child. Then force-refetch `/monitored_items/dependents/
     * children` so the slice actually reflects the new child — without
     * this step the detail page's own guarded mount fetch would skip
     * (hasLoaded is still true from the plan-members list visit). */
    setTimeout(async () => {
      try {
        await dispatch(refreshApToken());
        await dispatch(fetchChildItems(true));
      } catch (err) {
        logErrors(err);
      }
    }, TOKEN_REFRESH_DELAY_MS);
    return response as Invitor;
  };
}

// DELETE /api/client/invitors/children/{id} → removes the child from the
// family plan and returns the updated Invitor.
export function deleteChild(participantId: string) {
  return async (dispatch: AppDispatch) => {
    const response = await dispatch(
      deleteAPI({
        url: `${API_ENDPOINTS.invitorsChildById}/${participantId}`,
      }),
    ).unwrap();
    dispatch(setInvitor((response as Invitor) ?? null));
    /* Same reason as addChild — `administered_users` must drop the
     * removed child before the dependent-items fetch, or the backend
     * will keep returning the removed child's row. */
    setTimeout(async () => {
      try {
        await dispatch(refreshApToken());
        await dispatch(fetchChildItems(true));
      } catch (err) {
        logErrors(err);
      }
    }, TOKEN_REFRESH_DELAY_MS);
    return response as Invitor;
  };
}

// POST /api/client/invitors/newAdult → invite an aged-out child to
// SecureStart. Body carries `agedOutUserId` so the BE knows which existing
// participant this invite is upgrading.
export function inviteNewAdult(payload: InviteNewAdultPayload) {
  return async (dispatch: AppDispatch) => {
    const response = await dispatch(
      postAPI({ url: API_ENDPOINTS.invitorsNewAdult, data: payload }),
    ).unwrap();
    dispatch(setInvitor((response as Invitor) ?? null));
    return response as Invitor;
  };
}
